import { keepPreviousData, QueryClient, useQuery, UseQueryResult } from "@tanstack/react-query";
import { getPublicCollection, getPublicSmartShareCollection } from "../../api/collections";
import { PublicCollectionShareStatusResponse, ShareStatus, SmartShareLinkResponse, SmartShareType } from "@authory-packages/core";
import { usePublicCollectionAccessKeyContext } from "../../context/publicCollectionAccessKeyContext";
import { getSharedCollectionQuerie, getSmartSharesLinkQuerie } from "../../types/queries";
import { useRouter } from "next/router";
import { NextPageContext } from "next";
import { getSmartShareLink } from "../../api/smart-share";

export const getPublicCollectionClient = (
    isStandaloneCollectionView: boolean,
    useSmartShareEndpoint: boolean,
    collectionShareStatus?: PublicCollectionShareStatusResponse
) => {
    const router = useRouter();
    const authorSlug = router.query.author as string;
    const smartShareUrl = router.query.smartShareURL as string;
    const routeCollectionAccessKey = router.query.collectionAccessKey as string;

    const { collectionResolveKey, collectionAccessKey } = usePublicCollectionAccessKeyContext();

    let isCollectionSharedAndNotPassword = false;
    let isCollectionSharedAndHasPasswordAndVisitorSetPassword = false;

    if (isStandaloneCollectionView && collectionShareStatus) {
        const isCollectionShared = collectionShareStatus.shareStatus !== ShareStatus.Unpublished;
        isCollectionSharedAndNotPassword = isCollectionShared && !collectionShareStatus.isPasswordProtected;
        const isCollectionSharedAndHasPassword = isCollectionShared && collectionShareStatus.isPasswordProtected;
        isCollectionSharedAndHasPasswordAndVisitorSetPassword = isCollectionSharedAndHasPassword && !!collectionAccessKey?.length;
    }

    // profile collection page fetches straigh away, however standalone collection pages depends on publishing status
    const isCollectionReadyToFetch =
        !isStandaloneCollectionView || isCollectionSharedAndNotPassword || isCollectionSharedAndHasPasswordAndVisitorSetPassword;
    const collectionResolveKeyToUse = isStandaloneCollectionView ? (collectionResolveKey as string) : routeCollectionAccessKey;

    const collectionQuery = useSmartShareEndpoint
        ? getSharedCollectionQuerie(authorSlug, smartShareUrl, "")
        : getSharedCollectionQuerie(authorSlug, collectionResolveKeyToUse, collectionAccessKey);

    const collection = useQuery({
        queryKey: collectionQuery,
        queryFn: () =>
            useSmartShareEndpoint
                ? getPublicSmartShareCollection(collectionQuery[1]!, collectionQuery[2]!)
                : getPublicCollection(collectionQuery[1]!, collectionQuery[2]!, collectionQuery[3]!),
        enabled: isCollectionReadyToFetch,
        placeholderData: keepPreviousData,
        throwOnError: false
    });

    let collectionSmartShareLink: UseQueryResult<SmartShareLinkResponse, Error> | null = null;

    if (useSmartShareEndpoint) {
        const smartShareLinkQuerie = getSmartSharesLinkQuerie(authorSlug, smartShareUrl, SmartShareType.Collection);

        collectionSmartShareLink = useQuery({
            queryKey: smartShareLinkQuerie,
            queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3]),
            throwOnError: false,
            enabled: isCollectionReadyToFetch
        });
    }

    return { collection, collectionSmartShareLink };
};

export const getShareCollectionServer = async (queryClient: QueryClient, ctx: NextPageContext, useSmartShareEndpoint: boolean) => {
    const collectionAccessKey = ctx.query.collectionAccessKey as string;
    const smartShareUrl = ctx.query.smartShareURL as string;
    const authorSlug = ctx.query.author as string;
    const collectionQuery = getSharedCollectionQuerie(authorSlug, useSmartShareEndpoint ? smartShareUrl : collectionAccessKey, "");
    let smartShareLink: SmartShareLinkResponse | null = null;

    const col = await queryClient.fetchQuery({
        queryKey: collectionQuery,
        queryFn: () =>
            useSmartShareEndpoint
                ? getPublicSmartShareCollection(collectionQuery[1] as string, collectionQuery[2] as string)
                : getPublicCollection(collectionQuery[1] as string, collectionQuery[2] as string, "")
    });

    if (useSmartShareEndpoint) {
        const smartShareLinkQuerie = getSmartSharesLinkQuerie(authorSlug, smartShareUrl, SmartShareType.Collection);
        smartShareLink = await queryClient.fetchQuery({
            queryKey: smartShareLinkQuerie,
            queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3])
        });
    }

    return { col, smartShareLink };
};
